import * as React from 'react';

export interface DonutDatum {
  label: string;
  value: number;
  /** Hex / CSS colour for the slice + legend swatch. */
  color: string;
  /** Optional secondary text shown right-aligned in the legend (e.g. "$1.2M"). */
  secondary?: string;
}

interface DonutChartProps {
  data: DonutDatum[];
  /** Outer SVG size in px. */
  size?: number;
  /** Ring thickness in px. */
  thickness?: number;
  centerValue?: string;
  centerLabel?: string;
  /** Hide the legend (donut only). */
  hideLegend?: boolean;
  /** Show each slice's % of total in the legend. */
  showPct?: boolean;
}

/**
 * Lightweight, dependency-free SVG donut + legend. Deterministic sizing (no
 * ResponsiveContainer) so it renders identically inside any card width. Slices
 * with a zero value drop from the ring but remain in the legend.
 */
export function DonutChart({
  data,
  size = 132,
  thickness = 16,
  centerValue,
  centerLabel,
  hideLegend = false,
  showPct = false,
}: DonutChartProps) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const C = 2 * Math.PI * r;

  let cumulative = 0;
  const slices = data
    .filter((d) => d.value > 0)
    .map((d) => {
      const arc = total > 0 ? (d.value / total) * C : 0;
      const slice = {
        color: d.color,
        dasharray: `${arc} ${C - arc}`,
        dashoffset: -cumulative,
      };
      cumulative += arc;
      return slice;
    });

  return (
    <div className="tw-flex tw-items-center tw-gap-5">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="tw-shrink-0"
        role="img"
        aria-label={centerLabel ? `${centerValue ?? ''} ${centerLabel}` : 'Donut chart'}
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={thickness}
        />
        {slices.map((s, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={s.dasharray}
            strokeDashoffset={s.dashoffset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        ))}
        {centerValue ? (
          <text
            x={cx}
            y={centerLabel ? cy - 4 : cy}
            textAnchor="middle"
            dominantBaseline="central"
            className="tw-fill-foreground tw-tabular-nums"
            style={{ fontSize: 24, fontWeight: 700 }}
          >
            {centerValue}
          </text>
        ) : null}
        {centerLabel ? (
          <text
            x={cx}
            y={cy + 14}
            textAnchor="middle"
            className="tw-fill-muted-foreground"
            style={{ fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            {centerLabel}
          </text>
        ) : null}
      </svg>

      {hideLegend ? null : (
        <ul className="tw-m-0 tw-flex tw-min-w-0 tw-flex-1 tw-list-none tw-flex-col tw-gap-2 tw-p-0">
          {data.map((d) => {
            const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
            return (
              <li key={d.label} className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                <span
                  className="tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-sm"
                  style={{ backgroundColor: d.color }}
                  aria-hidden="true"
                />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-muted-foreground">
                  {d.label}
                </span>
                <span className="tw-font-semibold tw-tabular-nums tw-text-foreground">
                  {d.secondary ?? d.value}
                </span>
                {showPct ? (
                  <span className="tw-w-10 tw-text-right tw-text-xs tw-tabular-nums tw-text-muted-foreground">
                    {pct}%
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
